<template>
    <div class="bracket-seed">
        <table class="table is-hoverable is-fullwidth" v-if="editMode">
            <sortable :list="competitors" item-key="uniqueId" tag="tbody" :options="dragOptions" @update="onTableUpdate">
                <template v-slot:item="{ element: competitor, index }">
                    <tr class="draggable-table-item hoverable-button-container">
                        <td :class="getClassForIndex(index)">{{ index + 1 }}</td>
                        <td class="clarity-mask">{{ competitor.firstName }} {{ competitor.lastName }}</td>

                        <td @click="toggleAcademyColor(competitor.academy)" :class="getClassForAcademy(competitor.academy)">
                            <a>{{ competitor.academy }}</a>
                        </td>
                        <td>{{ competitor.branch }}</td>
                        <td>
                            <span v-if="isCompetitorRemoved(competitor.id)" class="tag is-danger">
                                {{ translate("common.competitior_deleted_from_bracket") }}
                            </span>

                            <span v-if="isCompetitorDisqualified(competitor.id)" class="tag is-warning"> Disqualified </span>
                        </td>
                    </tr>
                </template>
            </sortable>
        </table>
        <table class="table is-hoverable is-fullwidth" v-else>
            <tbody>
                <tr v-for="(competitor, index) in bracketCompetitors" :key="index">
                    <td :class="getClassForIndex(index)">{{ index + 1 }}</td>
                    <td class="clarity-mask">{{ competitor.firstName }} {{ competitor.lastName }}</td>
                    <td @click="toggleAcademyColor(competitor.academy)" :class="getClassForAcademy(competitor.academy)">
                        <a>{{ competitor.academy }}</a>
                    </td>
                    <td>{{ competitor.branch }}</td>
                    <td>
                        <span v-if="isCompetitorRemoved(competitor.id)" class="tag is-danger">
                            {{ translate("common.competitior_deleted_from_bracket") }}
                        </span>
                        <span v-if="isCompetitorDisqualified(competitor.id)" class="tag is-warning">
                            {{ translate("admin_show_bracket.competitor_disqualified") }}
                        </span>
                    </td>
                    <td v-if="addMissingMode">
                        <button
                            @click="addMissingCompetitorToIndex(index)"
                            class="button is-success is-small"
                            :disabled="competitor.id !== 0"
                        >
                            {{ translate("admin_show_bracket.add_here") }}
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
    import { Sortable } from "sortablejs-vue3";
    import translatorMixin from "../Common/translatorMixin";
    import rowColorCalculator from "./rowColorCalculator";

    export default {
        name: "table-bracket",
        components: {
            sortable: Sortable,
        },
        mixins: [translatorMixin],
        props: ["bracketCompetitors", "editMode", "addMissingMode", "removedCompetitors", "disqualifiedCompetitors"],
        data() {
            return {
                competitors: this.bracketCompetitors,
                bracketInitialized: false,
                coloredAcademies: {},
                rowsColoring: "",
                dragOptions: {
                    animation: 150,
                    scroll: true,
                    forceFallback: true,
                    bubbleScroll: true,
                },
            };
        },
        methods: {
            onTableUpdate(event) {
                const from = event.oldIndex;
                const to = event.newIndex;
                const item = this.competitors[from];

                this.competitors.splice(from, 1);
                this.competitors.splice(to, 0, item);
            },
            getCompetitors() {
                return this.competitors;
            },
            isCompetitorRemoved(competitorId) {
                if (!this.removedCompetitors) {
                    return false;
                }
                return this.removedCompetitors.indexOf(competitorId) >= 0;
            },
            isCompetitorDisqualified(competitorId) {
                if (!Array.isArray(this.disqualifiedCompetitors)) {
                    return false;
                }
                return this.disqualifiedCompetitors.indexOf(competitorId) >= 0;
            },
            getClassForIndex(index) {
                const partOfBracket = rowColorCalculator(this.rowsColoring, this.competitors, index);
                return partOfBracket ? "rows-coloring-" + partOfBracket : "";
            },
            getClassForAcademy(academyName) {
                const academyIndex = Object.keys(this.coloredAcademies).indexOf(academyName);
                const academyClassIndex = (academyIndex % 14) + 1;

                if (this.coloredAcademies[academyName]) {
                    return "academy-color-" + academyClassIndex;
                }
                return "";
            },
            toggleAcademyColor(academyName) {
                this.coloredAcademies[academyName] = !this.coloredAcademies[academyName];
            },
            setRowsColoring(rowsColoring) {
                this.rowsColoring = rowsColoring;
            },
            clearColoring() {
                this.rowsColoring = "";
                this.coloredAcademies = {};
            },
            addMissingCompetitorToIndex(index) {
                this.$emit("addMissingCompetitorToIndex", index);
            },
        },
    };
</script>

<style scoped>
    .flip-list-move {
        transition: transform 0.5s;
    }

    .no-move {
        transition: transform 0s;
    }

    .draggable-table-item {
        cursor: move;
    }

    .academy-color-1 {
        background-color: #e2aedd;
        color: #2b2e32;
    }

    .academy-color-2 {
        background-color: #7de2d1;
        color: #2b2e32;
    }

    .academy-color-3 {
        background-color: #4b88a2;
    }

    .academy-color-4 {
        background-color: #d3efbd;
        color: #2b2e32;
    }

    .academy-color-5 {
        background-color: #f3d9b1;
        color: #2b2e32;
    }

    .academy-color-6 {
        background-color: #983628;
    }

    .academy-color-7 {
        background-color: #d5ecd4;
        color: #2b2e32;
    }

    .academy-color-8 {
        background-color: #dd1155;
    }

    .academy-color-9 {
        background-color: #f8f272;
        color: #2b2e32;
    }

    .academy-color-10 {
        background-color: #c5a48a;
        color: #2b2e32;
    }

    .academy-color-11 {
        background-color: #30011e;
        color: #eeeeee;
    }

    .academy-color-12 {
        background-color: #6a8e7f;
        color: #2b2e32;
    }

    .academy-color-13 {
        background-color: #ff7f11;
        color: #2b2e32;
    }

    .academy-color-14 {
        background-color: #7fb069;
        color: #2b2e32;
    }

    .rows-coloring-1-2,
    .rows-coloring-1-4,
    .rows-coloring-1-8 {
        background-color: #7fb069;
        color: #2b2e32;
    }

    .rows-coloring-2-2,
    .rows-coloring-2-4,
    .rows-coloring-2-8 {
        background-color: #ff7f11;
        color: #2b2e32;
    }

    .rows-coloring-3-4,
    .rows-coloring-3-8 {
        background-color: #c5a48a;
        color: #2b2e32;
    }

    .rows-coloring-4-4,
    .rows-coloring-4-8 {
        background-color: #983628;
    }

    .rows-coloring-5-8 {
        background-color: #d5ecd4;
        color: #2b2e32;
    }

    .rows-coloring-6-8 {
        background-color: #4b88a2;
    }

    .rows-coloring-7-8 {
        background-color: #b5d49b;
        color: #2b2e32;
    }

    .rows-coloring-8-8 {
        background-color: #d9534f;
        color: white;
    }
</style>
